import React, { useContext, useEffect, useState, useCallback, useRef } from "react";
import { Link } from "gatsby";
import Layout from "../components/layout";
import PopularSearches from "../components/popular-searches/popular-searches.component";
import WhatHasChanged from "../components/what-has-changed/what-has-changed.component";
import AccordionGroup from "../components/accordion-group/accordion-group.component";
import FilterBar from "../components/filter-bar/filter-bar.component";
import Tabs from "../components/tabs/tabs.component";
import { MenuContext } from "../context";
import { RESIDENTAL_DATA_SCHEME } from "../data/criteria.data";
import { WHC_MONTHS, getTermByTitle } from "../components/what-has-changed/what-has-changed.utils";
import "../styles/app.scss";
import { triggerTealiumEvent } from "../utils/tealiumUtils";
import { useLocation } from "@reach/router";
import NewWindow from "../assets/new_window.inline.svg";
import ApplicationGuide from "../components/application-guide/application-guide.component";

const resultWord = (result) => {
  return result < 2 ? "result" : "results";
};

const Result = ({ result, searchTerm }) => (
  <p role="status" aria-atomic="true" className="result">
    {result !== null && searchTerm !== "" ? (
      <>
        Showing {result} {resultWord(result)} for <span>{searchTerm}</span>
      </>
    ) : null}
  </p>
);

const urlTabs = {
  0: {
    key: "tab",
    value: "residential",
  },
  1: {
    key: "tab",
    value: "buy-to-let",
  }
};

const decodeQueryParam = (param) => decodeURIComponent(param.replace(/\+/g, " "));

export default function Criteria(props) {
  const runRef = useRef(true);
  const [filterKey, setFilterKey] = useState("");
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [foundItemsCount, setFoundItemsCount] = useState(0);
  const [tabData, setTabData] = useState([[], []]);

  const menuContext = useContext(MenuContext);

  const scrollToToggle = useCallback(() => {
    let url = new URL(window.location);
    let scrollId = url.searchParams.get("p") || url.hash.substring(1) || "";
    // scroll to accordion
    if (scrollId) {
      // EDGE CASE: after first #key-word accordion click,
      // click works like redirect and reset tab to 0;
      // so we need to wait after tab change
      setTimeout(() => {
        const currentTabPanel = document.querySelector('[role="tabpanel"]:not([hidden])');

        if (currentTabPanel) {
          const divElement = currentTabPanel.querySelector('#' + CSS.escape(scrollId));

          if (divElement) {
            const accordionGroup = divElement.parentElement.closest('.accordion');
            if (accordionGroup) {
              accordionGroup.setAttribute("open", true);
            }
            divElement.setAttribute("open", true);
            setTimeout(() => {
              if (url.hash) {
                const section = currentTabPanel.querySelector('#' + CSS.escape(url.hash.substring(1)));
                const accordionGroup = section.parentElement.closest('.accordion');
                if (accordionGroup) {
                  accordionGroup.setAttribute("open", true);
                }
                if (section) {
                  section.scrollIntoView({ behavior: "smooth" });
                  return;
                }
              }
              divElement.scrollIntoView({ behavior: "smooth" });
            }, 500);
          }
        }
      }, 600);

      if (typeof window !== `undefined`) {
        //window.history.pushState("criteria", "Title", "/criteria");
      }
    }
  }, []);

  /*
  const crossSiteLinking = useEffect(() => {
    if (typeof window !== `undefined`) {
      let url = new URL(window.location);
      let scrollId = url.searchParams.get("p") || "";
      // scroll to accordion
      if (scrollId) {
        // EDGE CASE: after first #key-word accordion click,
        // click works like redirect and reset tab to 0;
        // so we need to wait after tab change
        setTimeout(() => {
          const divElement = document.getElementById(scrollId);
          if (divElement) {
            divElement.setAttribute("open", true);
            setTimeout(() => {
              divElement.scrollIntoView({ behavior: "smooth" });
            }, 500);
          }
        }, 600);
      }
      window.history.pushState("criteria", "Title", "/criteria");
    }
  }, []);
*/

  const onLocationChange = useCallback(
    (event) => {
      let url = new URL(document.location);
      let filterValue = url.searchParams.get("filter") || "";
      setFilterKey(filterValue);
      scrollToToggle();
      /* crossSiteLinking(); */
    },
    [scrollToToggle]
  );

  useEffect(() => {
    let { pathname, search } = props.location;
    if (pathname.includes("/criteria")) {
      if (search.trim() === "") {
        updateFilter("");
      } else {
        scrollToToggle();
      }
    }
  }, [props.location, scrollToToggle]);

  useEffect(() => {
    window.addEventListener("popstate", onLocationChange);

    return () => {
      window.removeEventListener("popstate", onLocationChange);
    };
  }, [onLocationChange]);

  const getFilterBar = () => <FilterBar setFilter={updateFilter} filterKey={filterKey} />;

  const updateFilter = (filterValue) => {
    // update browser history
    const url = new URL(window.location);
    if (filterValue) {
      url.searchParams.set("filter", filterValue);
      url.searchParams.delete("p");
      url.hash = '';
      url.searchParams.delete("tab");
      window.history.pushState({ filter: filterValue }, "", url);
    } else {
      url.searchParams.delete("filter");
      window.history.pushState({ filter: filterValue }, "", url);
    }

    // update filter
    setFilterKey(filterValue);
  };

  const addHandler = useCallback(() => {
    const ancors = [...document.querySelectorAll(`[data-accordion]`)];
    ancors.forEach(
      (ancor) =>
      (ancor.onclick = (event) => {
        event.preventDefault();
        const currentUrl = new URL(window.location);
        let filterValue = currentUrl.searchParams.get("filter");
        let p = ancor.getAttribute("data-accordion");
        let tab = ancor.getAttribute("data-tab");
        const url = new URL(window.location);
        url.searchParams.delete("p");
        url.hash = p;
        url.searchParams.set("tab", tab);
        window.history.pushState({ tab }, "", url);

        if (filterValue) {
          updateFilter('');
        }
        // need to change tab before scroll

        if (tab === "buy-to-let") {
          setSelectedTabIndex(1);
        } else {
          // residential tab
          setSelectedTabIndex(0);
        }
        scrollToToggle();
      })
    );
  }, [scrollToToggle]);

  const oversizedTable = useCallback(() => {
    let table1 = document.querySelector("#otc-1");
    let table2 = document.querySelector("#otc-2");

    if (table1) {
      table1.addEventListener("mouseenter", function () {
        table1.classList.add("active");
      });
      table1.addEventListener("mouseleave", function () {
        table1.classList.remove("active");
      });
    }
    if (table2) {
      table2.addEventListener("mouseenter", function () {
        table2.classList.add("active");
      });
      table2.addEventListener("mouseleave", function () {
        table2.classList.remove("active");
      });
    }
  }, []);

  const location = useLocation();

  useEffect(() => {
    setTimeout(() => {
      const url = new URL(window.location);
      const currentTab = url.searchParams.get("tab");
      let tabIndex = 0;
      if (currentTab === "buy-to-let") {
        setSelectedTabIndex(1);
        tabIndex = 1;
      } else {
        setSelectedTabIndex(0);
        tabIndex = 0;
      }
      url.searchParams.set("tab", urlTabs[tabIndex].value);
      window.history.pushState({ tab: urlTabs[tabIndex].value }, "", url);
      runRef.current = false;
    }, 500);

    if (typeof window !== "undefined" && window.utag) {
      triggerTealiumEvent({
        tealiumEvent: "view",
        pageUrl: location.href,
        pageType: "product information",
        pageName: "criteria",
        pageSubCategory: "criteria",
        rawDataLayer: "10451v11",
      });
    }
  }, [location.href]);

  /**
   * @name changeTab
   *
   * Change the selected tab
   * @param index this is the number which tab we would like to select. We have to tab for at the moment.0 = residential, 1 = let
   */
  const changeTab = useCallback(
    (index) => {
      let filteredData = [RESIDENTAL_DATA_SCHEME[0], RESIDENTAL_DATA_SCHEME[1]];
      let foundItems = [0, 0];

      filteredData = filteredData.map((tabData, tabIndex) => {
        return tabData.reduce((acc, accordionGroup) => {
          const accordionList = accordionGroup.accordionList.filter(({ filterTerm }) => {
            const terms = filterTerm.split(",").map((term) => term.trim().toLowerCase());
            // replace What Has Changed title with term on search only
            if (WHC_MONTHS.includes(filterKey)) {
              return terms.some((term) => term.includes(getTermByTitle(filterKey)));
            }
            if (filterKey) {
              return terms.some((term) => term === filterKey.toLowerCase());
            }
            return terms.some((term) => term.includes(filterKey.toLowerCase()));
          });

          if (accordionList.length) {
            foundItems[tabIndex] += accordionList.length;
            acc.push({ ...accordionGroup, accordionList });
          }

          return acc;
        }, []);
      });

      setTabData(filteredData);

      if (foundItems[index] === 0 && foundItems[1 - index] > 0) {
        setSelectedTabIndex(1 - index);
      } else {
        setSelectedTabIndex(index);
      }

      setFoundItemsCount(foundItems);

      // add click handlers on tab change for new accordions
      setTimeout(addHandler, 1000);
      setTimeout(oversizedTable, 1000);

      if (!runRef.current) {
        const url = new URL(window.location);
        url.searchParams.set("tab", urlTabs[index].value);
        url.hash = '';
        window.history.pushState({ tab: urlTabs[index].value }, "", url);
      }
    },
    [filterKey, addHandler, oversizedTable]
  );

  //Buy To Let filtering by tags
  //all accordions of BTL
  const buyToLetAccordionList = RESIDENTAL_DATA_SCHEME[1].map((e) => {
    return e.accordionList;
  });
  //merging into an array
  const mergedBTLAccordionList = [].concat.apply([], buyToLetAccordionList);
  //filter only for those who has filterTerm
  const allFilterTerms = mergedBTLAccordionList.filter((e) => {
    return e.filterTerm;
  });
  //getting all tags and merge into one array
  const tags = allFilterTerms.map((e) => e.filterTerm);

  let mergedTags = [].concat.apply([], tags);
  //TODO Plese find a better solution for this: replace [2] and [3], etc.
  let splitApplicantData = mergedTags[2].split(",");
  let splitBorrowingData = mergedTags[3].split(",");
  mergedTags = mergedTags.concat(splitApplicantData, splitBorrowingData);

  useEffect(() => {
    // Load the filter from the URL for the first load
    const url = new URL(window.location);
    let filterValue = url.searchParams.get("filter") || "";

    filterValue.toString();

    if (filterValue) {
      url.searchParams.delete("p");
      url.searchParams.delete("tab");
      setFilterKey(decodeQueryParam(filterValue));
    }
    changeTab(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeTab]);

  const handleClick = ({ eventContent, eventAction, rawDataLayer }) => {
    if (typeof window !== "undefined" && window.utag) {
      triggerTealiumEvent({
        tealiumEvent: "link",
        pageUrl: location.href,
        pageName: "criteria",
        eventCategory: "content",
        eventAction: eventAction,
        eventContent: eventContent,
        pageType: "product information",
        pageSubCategory: "criteria",
        rawDataLayer: rawDataLayer,
      });
    }
  };

  return (
    <>
      <Layout
        title="Criteria"
        metaDescription="To help you find what you\'re looking for, we've grouped our lending criteria into key categories. You can browse by category or use the search function to jump straight to the relevant entry."
      >
        <section className="criteria-page page-section row">
          <div className="information-container col-xs-12 col-lg-7">
            <h1 className="page-title">Criteria</h1>
            <p>
              To help you find what you're looking for, we've grouped our lending criteria into key categories. You can
              browse by category or use the search function to jump straight to the relevant entry.
            </p>
            <p>
              For any policy that is not specified on the Buy to let criteria tab, please instead refer to our Residential lending criteria tab.
            </p>
            <WhatHasChanged setFilter={updateFilter} />
            <ApplicationGuide />
            {getFilterBar()}
            <Result result={foundItemsCount[selectedTabIndex]} searchTerm={filterKey} />
            <Tabs
              activeTabIndex={selectedTabIndex}
              setActiveTab={changeTab}
              tabs={["Residential criteria", "Buy to let criteria"]}
              tabContent={[
                <div className="criteria-accordion">{tabData[0].map((groupData, index) => <AccordionGroup {...groupData} key={`res_${index}`} tab={urlTabs[0].value} />)}</div>,
                <div className="criteria-accordion">{tabData[1].map((groupData, index) => <AccordionGroup {...groupData} key={`buy_${index}`} tab={urlTabs[1].value} />)}</div>,
              ]}
            />

          </div>
          <div className="support-container col-xs-12 col-lg-4 col-lg-offset-1 margin-top-70">
            <div className="criteria-btn criteria-btn--light-green">
              <Link to="/frequently-asked-questions" inert={menuContext.isMenuOpen ? "true" : null}>
                Frequently asked questions
                <NewWindow />
              </Link>
            </div>
            <div className="criteria-btn">
              <Link to="/packaging-requirements" inert={menuContext.isMenuOpen ? "true" : null}>
                Packaging requirements
                <NewWindow />
              </Link>
            </div>
            <div className="criteria-btn criteria-btn--light-green">
              <Link to="/resources" inert={menuContext.isMenuOpen ? "true" : null}>
                Resources
                <NewWindow />
              </Link>
            </div>
          </div>
        </section>
      </Layout>
    </>
  );
}
